import React, {createRef, ReactNode, RefObject} from "react";
import {
	AuthProvider,
	Information,
	OAuthProviderName,
	UserCredentials,
	UserStorage
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import StorageItem from "../StorageItem";
import azure from "../../../resources/images/oauth/azure.svg";
import auth0 from "../../../resources/images/oauth/auth0.svg";
import Icon from "../components/Icon";
import FunctionHelper from "../helper/FunctionHelper";
import DomHelper from "../helper/DomHelper";
import {MenuBarProperties} from "./AbstractMenuBarInterface";
import PortalPureComponent from "../components/PortalPureComponent";

/**
 * An AbstractMenuBar component is used to bundle functions used in menu bars.
 */
export default abstract class AbstractMenuBar<P extends MenuBarProperties, S> extends PortalPureComponent<P, S> {
	private resizeObserver: ResizeObserver;
	protected resizeRoot: RefObject<HTMLDivElement>;
	protected resizeContainer: RefObject<HTMLDivElement>;

	/**
	 * Constructs an {@link AbstractMenuBar} instance
	 */
	constructor(props: Readonly<P>) {
		super(props);

		this.resizeRoot = createRef<HTMLDivElement>();
		this.resizeContainer = createRef<HTMLDivElement>();
		this.resizeObserver = new ResizeObserver((entries: Array<ResizeObserverEntry>) => {
			for (let entry of entries) {
				let element: HTMLElement = entry.target as HTMLElement;
				this.resizeMenu(element);
			}
		});
	}

	/**
	 * This is called when the component is mounted
	 */
	public async componentDidMount(): Promise<void> {
		await super.componentDidMount();

		if (FunctionHelper.exists(this.resizeRoot.current)) {
			this.resizeObserver.observe(this.resizeRoot.current!, {
				box: "border-box"
			});
		}
	}

	/**
	 * This is called when the component is unmounted
	 */
	public async componentWillUnmount(): Promise<void> {
		await super.componentWillUnmount();
		this.resizeObserver?.disconnect();
	}

	/**
	 * This is called when the components props or state is updated
	 */
	public async componentDidUpdate(prevProps: Readonly<P>, prevState: Readonly<S>, snapshot?: any): Promise<void> {
		if (FunctionHelper.exists(this.resizeRoot.current)) {
			this.resizeMenu(this.resizeRoot.current!);
		}
	}

	/**
	 * Handle login button click
	 * @private
	 */
	protected async handleLogin(): Promise<void> {
		this.props.context.getPage().setState({
			showLoginDialog: true,
			loginParameter: undefined
		});
	}

	/**
	 * Handle logout button click
	 * @private
	 */
	protected async handleLogout(): Promise<void> {
		this.props.context.getPage().setState({
			showLogoutDialog: true
		});
	}

	/**
	 * Returns the {@link AuthProvider} used for the authentication if one was used
	 */
	protected getProvider(): AuthProvider | undefined {
		let portalInfo: Information = this.props.context.info;

		let providerName: string | null = localStorage.getItem(StorageItem.PROVIDER);
		for (let oauthProvider of FunctionHelper.applyValue(portalInfo.oauth, Information.getOauthDefault())) {
			if (oauthProvider.name === providerName) {
				return oauthProvider;
			}
		}
	}

	/**
	 * Renders the user icon as {@link ReactNode}. Depending on the {@link AuthProvider} and {@link UserStorage} used
	 * for login other icons will be shown.
	 */
	protected renderUserIcon(provider?: AuthProvider): ReactNode {
		let portalInfo: Information = this.props.context.info;
		let user: UserCredentials | undefined = this.props.context.user;

		switch (provider?.name) {
			case OAuthProviderName.AzureAd:
				return <img src={azure} className="p-button-icon" alt={provider.name}/>;
			case OAuthProviderName.Auth0:
				return <img src={auth0} className="p-button-icon" alt={provider.name}/>;
		}

		switch (portalInfo.localAuth?.userStorage) {
			case UserStorage.AzureAd:
				return <img src={azure} className="p-button-icon" alt="Microsoft"/>;
			case UserStorage.Ldap:
				return <Icon icon={"p-button-icon p-c p-button-icon-left icon-user ldap"}/>;
			case UserStorage.Database:
				return <Icon icon={"p-button-icon p-c p-button-icon-left icon-user database"}/>;
		}

		if (FunctionHelper.applyValue(user?.isAdmin, UserCredentials.getIsAdminDefault())) {
			return <Icon icon={"p-button-icon p-c p-button-icon-left icon-user admin"}/>;
		}

		return <Icon icon={"p-button-icon p-c p-button-icon-left icon-user"}/>;
	}

	/**
	 * Resizes the **.menu-group** items in the {@link AbstractMenuBar.resizeContainer} depending on the given
	 * {@link HTMLElement} content and scrolling width.
	 */
	private resizeMenu(element: HTMLElement): void {
		if (!FunctionHelper.exists(this.resizeContainer.current)) {
			return;
		}

		let firstChild: HTMLElement | undefined = DomHelper.findAll(
			this.resizeContainer.current, ".menu-group.small"
		)[0];

		while (element.offsetWidth > element.scrollWidth && FunctionHelper.exists(firstChild)) {
			DomHelper.removeClass(firstChild, "small");
			firstChild = DomHelper.findAll(
				this.resizeContainer.current, ".menu-group.small"
			).pop();
		}

		let lastChild: HTMLElement | undefined = DomHelper.findAll(
			this.resizeContainer.current, ".menu-group:not(.small)"
		).pop();

		while (element.offsetWidth < element.scrollWidth && FunctionHelper.exists(lastChild)) {
			DomHelper.addClass(lastChild, "small");
			lastChild = DomHelper.findAll(
				this.resizeContainer.current, ".menu-group:not(.small)"
			).pop();
		}
	}
}